import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatBRL } from "@/lib/api";
import ImageGallery from "@/components/ImageGallery";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { Minus, Plus, ShieldCheck, Truck, RotateCcw } from "lucide-react";

export default function ProductPage() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const { add, setOpen } = useCart();
  const nav = useNavigate();

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setP(r.data);
      if ((r.data.sizes || []).length) setSize(r.data.sizes[0]);
      if ((r.data.colors || []).length) setColor(r.data.colors[0]);
    });
  }, [id]);

  if (!p) {
    return (
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14 grid md:grid-cols-2 gap-10">
        <div className="aspect-[4/5] shimmer" />
        <div className="space-y-4">
          <div className="h-10 shimmer w-3/4" />
          <div className="h-6 shimmer w-1/3" />
          <div className="h-32 shimmer w-full" />
        </div>
      </div>
    );
  }

  const hasSale = p.sale_price && p.sale_price < p.price;
  const price = hasSale ? p.sale_price : p.price;
  const outOfStock = (p.stock ?? 0) <= 0;

  const handleAdd = (buyNow = false) => {
    if ((p.sizes || []).length && !size) {
      toast.error("Selecione um tamanho");
      return;
    }
    add(p, { size, color, quantity: qty });
    toast.success(`${p.name} adicionado ao carrinho`);
    if (buyNow) {
      setOpen(false);
      nav("/checkout");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-8 py-10">
      <nav className="text-xs tracking-widest uppercase text-zinc-500 mb-8" data-testid="product-breadcrumb">
        <a href="/" className="hover:text-white">Início</a> / {p.name}
      </nav>
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <ImageGallery images={p.images || []} name={p.name} />
        <div className="lg:sticky lg:top-24 self-start">
          <div className="overline-label">{p.sku}</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter text-white mt-3" data-testid="product-name">{p.name}</h1>
          <div className="flex items-baseline gap-3 mt-5">
            <span className="font-display text-3xl text-white" data-testid="product-price">{formatBRL(price)}</span>
            {hasSale && <span className="strike-price text-base">{formatBRL(p.price)}</span>}
          </div>
          <p className="text-sm text-zinc-400 mt-1">em até 6x de {formatBRL(price / 6)} sem juros</p>

          {p.description && (
            <p className="text-zinc-300 mt-6 leading-relaxed max-w-xl" data-testid="product-desc">{p.description}</p>
          )}

          {(p.sizes || []).length > 0 && (
            <div className="mt-8">
              <div className="overline-label mb-3">Tamanho</div>
              <div className="flex flex-wrap gap-2">
                {p.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`min-w-12 h-11 px-4 border text-sm font-medium transition-colors ${
                      size === s ? "bg-white text-black border-white" : "border-white/20 text-white hover:border-white"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {(p.colors || []).length > 0 && (
            <div className="mt-6">
              <div className="overline-label mb-3">Cor</div>
              <div className="flex flex-wrap gap-2">
                {p.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    data-testid={`color-${c}`}
                    className={`px-4 h-10 border text-sm transition-colors ${
                      color === c ? "bg-white text-black border-white" : "border-white/20 text-white hover:border-white"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6">
            <div className="overline-label mb-3">Quantidade</div>
            <div className="inline-flex items-center border border-white/20">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="h-11 w-11 grid place-items-center hover:bg-white/5" data-testid="qty-dec"><Minus size={14} /></button>
              <span className="min-w-10 text-center text-white">{qty}</span>
              <button onClick={() => setQty((q) => q + 1)} className="h-11 w-11 grid place-items-center hover:bg-white/5" data-testid="qty-inc"><Plus size={14} /></button>
            </div>
            <span className="ml-4 text-xs text-zinc-500">
              {outOfStock ? "Sem estoque" : `${p.stock} em estoque`}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button
              data-testid="add-to-cart-btn"
              onClick={() => handleAdd(false)}
              disabled={outOfStock}
              className="flex-1 bg-white text-black hover:bg-zinc-200 h-14 tracking-widest uppercase text-xs"
            >
              Adicionar ao carrinho
            </Button>
            <Button
              data-testid="buy-now-btn"
              onClick={() => handleAdd(true)}
              disabled={outOfStock}
              variant="outline"
              className="flex-1 bg-transparent border-white/30 text-white hover:bg-white/10 h-14 tracking-widest uppercase text-xs"
            >
              Comprar agora
            </Button>
          </div>

          <div className="mt-10 border-t border-white/10 pt-6 space-y-3 text-sm text-zinc-400">
            <div className="flex items-center gap-3"><Truck size={16} className="text-white" /> Frete para todo o Brasil</div>
            <div className="flex items-center gap-3"><ShieldCheck size={16} className="text-white" /> Compra 100% segura</div>
            <div className="flex items-center gap-3"><RotateCcw size={16} className="text-white" /> Troca em 7 dias</div>
          </div>
        </div>
      </div>
    </div>
  );
}
